import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import styles from './dataVendorDetail.module.scss';
import { DataVendor, DrmPage, DataVendorDetailsTabType } from 'v2/models';
import useGetCustomTabs from 'v2/hooks/useGetCustomTabs';
import Avatar from 'v2/Avatar/Avatar';
import avatarPersonNoProfilePic from '../../images/avatarPersonNoProfilePic.png';
import DataVendorActions from 'v2/pages/DataVendorDetails/DataVendorActions';
import { FormGroup } from 'react-bootstrap';
import { Tab, Tabs } from '@material-ui/core';
import EditDataVendorModal from 'v2/common/DataVendorModals/EditDataVendorModal/EditDataVendorModal';
import EditDataVendorDescriptionModal from 'v2/common/DataVendorModals/EditDescriptionModal/EditDescriptionModal';
import EditDataVendorWebsiteModal from 'v2/common/DataVendorModals/EditWebsiteModal/EditWebsiteModal';
import SectionHeader from 'v2/common/DRM/SectionHeader/SectionHeader';
import HtmlRenderer from 'v2/common/HtmlRenderer/HtmlRenderer';
import DataVendorTabContent from './DataVendorTabContent/DataVendorTabContent';
import { useTranslation } from 'react-i18next';
import { TranslationKeys } from 'translations';
import CopyEntityButton from 'v2/common/CopyEntityButton/CopyEntityButton';

function useTabs() {
  const { t } = useTranslation();

  const TABS: DataVendorDetailsTabType[] = [
    {
      eventKey: 'contacts',
      title: t(TranslationKeys.contact_other),
    },
    {
      eventKey: 'activity',
      title: `activity`,
    },
    {
      eventKey: 'details',
      title: `details`,
    },
    {
      eventKey: 'files',
      title: `files`,
    },
  ];

  return TABS;
}

interface DataVendorDetailProps {
  dataVendor: DataVendor;
  className?: string;
  refetch?: () => void;
}

const DataVendorDetail = ({
  dataVendor,
  className,
  refetch,
}: DataVendorDetailProps) => {
  const TABS = useTabs();
  const defaultTab = TABS[0].eventKey;
  const [selectedTab, setSelectedTab] =
    useState<DataVendorDetailsTabType['eventKey']>(defaultTab);
  const handleTabChange = (
    event: React.ChangeEvent<unknown>,
    newValue: DataVendorDetailsTabType['eventKey']
  ) => {
    setSelectedTab(newValue || defaultTab);
  };
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditDescriptionModal, setShowEditDescriptionModal] =
    useState(false);
  const [showEditWebsiteModal, setShowEditWebsiteModal] = useState(false);
  const [localDataVendor, setLocalDataVendor] = useState(dataVendor);

  const { data: customTabs } = useGetCustomTabs(DrmPage.DATA_VENDOR);

  useEffect(() => {
    setLocalDataVendor(dataVendor);
  }, [dataVendor]);

  const handleEditClose = (data?: DataVendor) => {
    setShowEditModal(false);
    setShowEditDescriptionModal(false);
    setShowEditWebsiteModal(false);
    if (data) {
      setLocalDataVendor(data);
    }
  };

  return (
    <>
      <div
        className={classnames(
          'container-xl',
          'h-100',
          styles.dataVendorDetailsContainer,
          className
        )}
      >
        <div
          className={classnames(
            'pr-4 d-flex fix-bg-images pl-4',
            styles.leftContent
          )}
        >
          <FormGroup>
            <div className="d-flex flex-row">
              <Avatar
                url={localDataVendor.avatar_url || avatarPersonNoProfilePic}
                className={styles.avatar}
                initials={localDataVendor.name[0]}
              />
              <div
                className={classnames(
                  'ml-3 d-flex flex-column',
                  styles.nameContainer
                )}
              >
                {localDataVendor.name && (
                  <label className={styles.name}>
                    {localDataVendor.name} <CopyEntityButton />
                  </label>
                )}
                {localDataVendor.classification && (
                  <label className={styles.classification}>
                    {localDataVendor.classification}
                  </label>
                )}
              </div>
              <div
                className={classnames(
                  'mt-4 d-flex flex-column',
                  styles.actions
                )}
              >
                <DataVendorActions
                  onChange={() => setShowEditModal(true)}
                  dataVendor={localDataVendor}
                />
              </div>
            </div>
            <div
              className="d-flex flex-column"
              data-cy={`datavendor:website-and-description`}
            >
              <SectionHeader
                title="Website"
                className="mt-5 mb-2"
                isEditable={localDataVendor.is_editable}
                onClick={() => setShowEditWebsiteModal(true)}
              />
              {localDataVendor.domain && (
                <HtmlRenderer source={localDataVendor.domain} />
              )}
              <SectionHeader
                title="Description"
                isEditable={localDataVendor.is_editable}
                className={styles.description}
                onClick={() => setShowEditDescriptionModal(true)}
              />
              {localDataVendor.description && (
                <HtmlRenderer source={localDataVendor.description} />
              )}
            </div>
          </FormGroup>
        </div>

        <div className={classnames(styles.tabs_container)}>
          <Tabs
            className={classnames(styles.scrolling_tabs)}
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            data-cy="datavendor:tabs"
          >
            {TABS.map((item) => (
              <Tab
                key={item.eventKey}
                className={classnames(styles.tabs)}
                value={item.eventKey}
                label={`${item.title}`}
                disableRipple
              ></Tab>
            ))}
            {customTabs?.results.map((tab) => (
              <Tab
                key={tab.id}
                className={classnames(styles.tabs)}
                value={tab.name}
                label={`${tab.name}`}
                disableRipple
              ></Tab>
            ))}
          </Tabs>
          <div>
            <DataVendorTabContent
              dataVendor={localDataVendor}
              selectedTab={selectedTab}
              customTabs={customTabs}
              refetch={refetch}
              TABS={TABS}
            />
          </div>
        </div>
      </div>
      <EditDataVendorModal
        show={showEditModal}
        dataVendor={localDataVendor}
        onClose={handleEditClose}
      />
      <EditDataVendorDescriptionModal
        show={showEditDescriptionModal}
        dataVendor={localDataVendor}
        onClose={handleEditClose}
      />
      <EditDataVendorWebsiteModal
        show={showEditWebsiteModal}
        dataVendor={localDataVendor}
        onClose={handleEditClose}
      />
    </>
  );
};

export default DataVendorDetail;
